import {
  faCalendar,
  faMoneyBill,
  faStopwatch,
} from '@fortawesome/pro-regular-svg-icons';
import {
  Button,
  ElapsedTime,
  Heading,
  Icon,
  Text,
} from '@quality24/design-system';
import {
  Alert,
  HospitalDepartment,
  PaymentRequest,
  Service,
  ServiceGroupItem,
} from '@quality24/inpatient-typings';
import classNames from 'classnames';
import * as React from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import PaymentStatusBadge from '@/inpatient-patient-pwa/atoms/PaymentStatusBadge';

interface ExtendedAlert extends Required<Pick<Alert, 'id' | 'createdAt'>> {
  group: Pick<ServiceGroupItem, 'id' | 'name' | 'nameI18N'> & {
    service: Required<Pick<Service, 'id' | 'reason' | 'reasonI18N'>> & {
      department: Required<
        Pick<HospitalDepartment, 'id' | 'name' | 'nameI18N'>
      >;
    };
  };
  paymentRequest?: Pick<PaymentRequest, 'id' | 'name' | 'price' | 'status'>;
}

export interface Props {
  className?: string;
  alert: ExtendedAlert;
  hideElapsedTime?: boolean;
}

const ActiveAlertCard: React.FunctionComponent<Props> = ({
  className,
  alert,
  hideElapsedTime,
}) => {
  const { t } = useTranslation();
  const { group } = alert;
  const navigate = useNavigate();

  return (
    <div
      className={classNames(
        className,
        'rounded-md bg-white p-3 border border-2 border-primary d-flex flex-column gap-2',
      )}
      role="listitem"
    >
      <div className="d-flex flex-items-center gap-3">
        <Heading as="h3" weight="semiBold">
          {group.service?.department?.nameI18N ||
            group.service?.department?.name}{' '}
          {alert.paymentRequest && (
            <PaymentStatusBadge status={alert.paymentRequest.status} />
          )}
        </Heading>
      </div>
      <div className="row">
        <div className="col-6 col-sm-8 col-md-10">
          <Text>{group.nameI18N || group.service?.reasonI18N}</Text>
        </div>
        <div className="col-6 col-sm-4 col-md-2 d-flex align-items-center gap-2">
          <Icon
            icon={!hideElapsedTime ? faStopwatch : faCalendar}
            color="primary"
          />
          {!hideElapsedTime ? (
            <ElapsedTime start={alert.createdAt} />
          ) : (
            <Text>{moment(alert.createdAt).format('L')}</Text>
          )}
        </div>
      </div>
      {alert.paymentRequest && (
        <div className="d-flex">
          <Button
            fluid
            icon={faMoneyBill}
            onClick={() => navigate(`/calls/${alert.id}`)}
          >
            {t('alerts.viewDetails')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ActiveAlertCard;
